import React from 'react';
import { useGameStore } from '../../store/gameStore';

interface Props {
  send: (action: string, data?: object) => void;
}

export function GameEndScreen({ send }: Props) {
  const { playerName, role, players, reset } = useGameStore();

  // Generate final scores (mock data)
  const finalScores = players
    .map(p => ({
      ...p,
      totalScore: Math.floor(Math.random() * 1000) + 1500,
      chefScore: Math.floor(Math.random() * 600) + 700,
      customerScore: Math.floor(Math.random() * 600) + 700,
    }))
    .sort((a, b) => b.totalScore - a.totalScore);

  const winner = finalScores[0];
  const myStats = finalScores.find(p => p.playerName === playerName);
  const myRank = finalScores.findIndex(p => p.playerName === playerName) + 1;

  const handlePlayAgain = () => {
    reset();
  };

  return (
    <div className="screen">
      <div style={{ textAlign: 'center', width: '100%', maxWidth: 800 }}>
        {/* Trophy Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 80, marginBottom: 16 }}>🏆</div>
          <h1 style={{ 
            fontSize: 36, 
            fontWeight: 700, 
            color: 'var(--ltc-primary)',
            marginBottom: 8,
          }}>
            GAME COMPLETE!
          </h1>
          <p style={{ color: 'var(--ltc-text-secondary)', fontSize: 18 }}>
            Week 1 has ended - Final Results
          </p>
        </div>

        {/* Winner Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
          color: 'white',
          padding: 40,
          borderRadius: 16,
          marginBottom: 32,
        }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>🥇 OVERALL WINNER</div>
          <div style={{ fontSize: 48, fontWeight: 700, marginBottom: 12 }}>
            {winner.playerName}
          </div>
          <div style={{ fontSize: 24 }}>
            Total Score: {winner.totalScore} points
          </div>
        </div>

        {/* Category Winners */}
        <h3 style={{ color: 'var(--ltc-primary)', marginBottom: 20 }}>
          🎖️ Category Winners
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          marginBottom: 32,
        }}>
          <div style={{
            background: '#e8f5e9',
            padding: 20,
            borderRadius: 12,
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>👨‍🍳</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Best Chef</div>
            <div style={{ color: '#2e7d32', fontWeight: 700 }}>
              {finalScores.sort((a, b) => b.chefScore - a.chefScore)[0].playerName}
            </div>
          </div>
          <div style={{
            background: '#e3f2fd',
            padding: 20,
            borderRadius: 12,
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🍽️</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Best Customer</div>
            <div style={{ color: '#1565c0', fontWeight: 700 }}>
              {finalScores.sort((a, b) => b.customerScore - a.customerScore)[0].playerName}
            </div>
          </div>
          <div style={{
            background: '#fff3e0',
            padding: 20,
            borderRadius: 12,
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⚡</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Speed Demon</div>
            <div style={{ color: '#e65100', fontWeight: 700 }}>
              {playerName}
            </div>
          </div>
          <div style={{
            background: '#f3e5f5',
            padding: 20,
            borderRadius: 12,
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>😊</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Happy Customers</div>
            <div style={{ color: '#6a1b9a', fontWeight: 700 }}>
              {finalScores[1]?.playerName || playerName}
            </div>
          </div>
        </div>

        {/* Your Stats */}
        <h3 style={{ color: 'var(--ltc-primary)', marginBottom: 20 }}>
          📈 Your Final Statistics
        </h3>
        <div style={{
          background: 'var(--ltc-bg-secondary)',
          padding: 24,
          border: 'var(--ltc-border)',
          textAlign: 'left',
          marginBottom: 32,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div><strong>Total Score:</strong> {myStats?.totalScore || 0} points</div>
            <div><strong>Final Rank:</strong> #{myRank} of {players.length}</div>
            <div><strong>Chef Score:</strong> {myStats?.chefScore || 0} points</div>
            <div><strong>Customer Score:</strong> {myStats?.customerScore || 0} points</div>
            <div><strong>Orders Completed:</strong> 28</div>
            <div><strong>Perfect Orders:</strong> 15 (54%)</div>
            <div><strong>Avg Completion Time:</strong> 4:52</div>
            <div><strong>Customer Satisfaction:</strong> ⭐ 4.3</div>
          </div>
        </div>

        {/* Final Leaderboard */}
        <div style={{
          background: 'var(--ltc-bg-card)',
          border: 'var(--ltc-border)',
          marginBottom: 32,
          overflow: 'hidden',
        }}>
          <div style={{
            background: 'var(--ltc-primary)',
            color: 'white',
            padding: '12px 16px',
            display: 'flex',
            fontWeight: 600,
          }}>
            <div style={{ width: 50 }}>Rank</div>
            <div style={{ flex: 1 }}>Player</div>
            <div style={{ width: 100, textAlign: 'right' }}>Total</div>
          </div>
          {finalScores.map((player, idx) => (
            <div key={player.playerId} style={{
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              background: player.playerName === playerName ? '#ffe8d6' : (idx % 2 ? '#f9f9f9' : 'white'),
              borderBottom: '1px solid #eee',
            }}>
              <div style={{ width: 50, fontWeight: 700, fontSize: 18 }}>
                {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}`}
              </div>
              <div style={{ flex: 1, fontWeight: player.playerName === playerName ? 600 : 400 }}>
                {player.playerName}
                {player.playerName === playerName && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--ltc-accent)' }}>
                    (You)
                  </span>
                )}
              </div>
              <div style={{ 
                width: 100, 
                textAlign: 'right', 
                fontWeight: 700,
                color: 'var(--ltc-primary)',
              }}>
                {player.totalScore}
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
          <button
            className="btn btn-accent btn-large"
            onClick={handlePlayAgain}
          >
            🎮 Play Again
          </button>
          <button
            className="btn btn-secondary btn-large"
            onClick={() => window.location.reload()}
          >
            📊 Share Results
          </button>
        </div>
      </div>
    </div>
  );
}
